import { useDispatch } from 'react-redux'
import { type NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import { useAsync } from 'react-async-hook'
import uniq from 'lodash/uniq'
import { Form, Formik, FormikProps } from 'formik'
import Grid from '@mui/material/Grid'

import { createUser, removeUser, updateUser } from '../../redux/actions/userActions'
import { Group, NewUser, Role } from 'common/api/v1/types'
import { Api, AppDispatch, useRoutes } from '../../store'
import { Autocomplete, ButtonsPane, Paper, SafeRouting, Select, TextInput } from '../common/Form'
import Pendable from '../common/Pendable'
import Wrapper from '../common/Wrapper'
import { formTransform, useConfirmationDialog, useUser } from '../../utils'
import { EnrichedUser, ExistingUserForUpdate } from '../../api/nm-types'
import { FailedFetchingContent } from '../common/FailedFetchingContent'
import { useCallback } from 'react'

type UserFormValues = Omit<EnrichedUser, 'role'> & {
  role: string
  password: string
}

const { userApi } = Api

const getInitialStateUpdating = (userToEdit: EnrichedUser): UserFormValues => ({
  password: '',
  ...userToEdit,
})

const getInitialStateCreating = (currentUser: EnrichedUser): UserFormValues => ({
  id: '',
  group: currentUser.group,
  role: '',
  username: '',
  password: '',
  _group: { id: '', name: '' },
})

const UserForm = (formik: FormikProps<UserFormValues>, navigate: NavigateFunction, isUpdating: boolean) => {
  const routes = useRoutes()
  const dispatch = useDispatch<AppDispatch>()
  const showConfirm = useConfirmationDialog()
  const user = useUser()

  let roleOptions: string[] = [Role.basic]
  switch (user.role) {
    case Role.admin:
      roleOptions.push(Role.admin)
      break
    case Role.super:
      roleOptions.push(Role.admin)
      if (user._group.id === formik.values.group) {
        roleOptions.push(Role.super)
      }
      break
  }
  if (isUpdating && formik.values.role) roleOptions = uniq([...roleOptions, formik.values.role])

  const deleteUser = () => {
    showConfirm(
      () => void removeUser(formik.values, { dispatch, navigate, userApi: Api.userApi, routes }),
      `Are you sure you want to delete user ${formik.values.username}?`,
    )
  }

  const groupsApi = useCallback(Api.groupsApi.getPureGroups.bind(Api.groupsApi), [])

  return (
    <Grid container>
      <Grid item xs={12}>
        <SafeRouting enabled={formik.dirty && !formik.isSubmitting} />
        <Form id="user-form" translate="no" noValidate>
          <Paper title="User settings">
            <TextInput
              name="username"
              label="Name"
              required
              type="text"
              validators={formik.initialValues.username === 'admin' ? {} : { username: {} }}
              autoFocus
              disabled={isUpdating}
              autoComplete="off"
            />
            <TextInput
              name="password"
              label="Password"
              required={!isUpdating}
              type="password"
              autoComplete="new-password"
              validators={{
                pwd: {},
              }}
            />

            {user.role === Role.super ? (
              <Autocomplete<Group>
                name="group"
                formik={formik}
                api={groupsApi}
                required={true}
                defaultOption={
                  isUpdating
                    ? {
                        id: formik.values._group.id,
                        name: formik.values._group.name,
                      }
                    : undefined
                }
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                optionComparator={(o1, o2) => o1.id === o2.id}
              />
            ) : (
              <Select
                name="group"
                label="Group"
                required
                options={[{ value: user._group.id, name: user._group.name }]}
                disabled
              />
            )}
            <Select name="role" label="Role" required options={roleOptions} />

            {isUpdating ? <TextInput name="_mfa._totp" label="MFA" type="text" disabled /> : null}
          </Paper>

          <ButtonsPane
            main={{
              Cancel: {
                onClick: () => navigate(routes.users()),
              },
              Save: { savingState: formik.isSubmitting, primary: true, type: 'submit' },
            }}
            secondary={
              isUpdating && user.id !== formik.values.id
                ? {
                    'Remove user': { onClick: deleteUser },
                  }
                : undefined
            }
          />
        </Form>
      </Grid>
    </Grid>
  )
}

export const Edit = () => {
  const { id } = useParams()
  const user = useUser()
  const routes = useRoutes()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const isUpdating = !!id
  const { result, loading, error } = useAsync(async () => (isUpdating ? userApi.getUser(id!) : undefined), [id])
  const userToEdit: EnrichedUser | undefined = result
    ? {
        ...result,
        _mfa: { _totp: result.mfa?.totp?.enabled ? 'Configured' : 'Not configured' },
      }
    : undefined

  const onSubmit = async (user: ExistingUserForUpdate | NewUser) => {
    if (isUpdating) {
      await updateUser(user as ExistingUserForUpdate, { dispatch, userApi: Api.userApi, navigate, routes })
    } else {
      await createUser(user as NewUser, { dispatch, userApi: Api.userApi, navigate, routes })
    }
  }

  return (
    <Wrapper name={['Users', id ? userToEdit?.username : 'New']}>
      <Grid container spacing={0}>
        {error ? (
          <FailedFetchingContent message={'Failed to fetch user'} />
        ) : (
          <Pendable pending={loading}>
            <Formik
              onSubmit={(values) => {
                return onSubmit(formTransform(values))
              }}
              initialValues={
                isUpdating && userToEdit ? getInitialStateUpdating(userToEdit) : getInitialStateCreating(user)
              }
            >
              {(formik) => UserForm(formik, navigate, isUpdating)}
            </Formik>
          </Pendable>
        )}
      </Grid>
    </Wrapper>
  )
}
